/**
 * Live map-viewport store — a Zustand snapshot of the Map's **current**
 * camera (``{ center: { lon, lat }, zoom }``) plus a "preview home"
 * imperative-request channel, the bridge that lets the Profile editor's
 * Map section (increment 8b) capture / re-fly the operator's home viewport
 * without threading the MapLibre instance through React (ADR-033 §9.4;
 * REQ-UI-076 / REQ-UI-090).
 *
 * # Why a store
 *
 * The Profile editor is a sibling of the Map in the tree — it has no handle
 * on the live MapLibre camera. The editor's **"Set home to current view"**
 * button needs the camera's current ``center`` + ``zoom`` to write into
 * ``draft.map.home``; its **"Preview home"** button needs to ask the Map to
 * re-fly to the draft home. A small module-level Zustand store (mirroring
 * :file:`map/track-store.ts` / :file:`map/geometry-store.ts`) is the
 * smallest tool that decouples the two: the Map *publishes* its camera here
 * on every settled pan/zoom; the editor *reads* it for capture and
 * *requests* a preview-home re-fly through it.
 *
 * # Two channels, one store
 *
 *   1. ``viewport`` — the Map's latest camera, ``null`` until the map first
 *      loads (so the editor disables "Set home to current view" while the
 *      map isn't ready). Published by :file:`Map.tsx` on its existing
 *      ``onMapReady`` + settled-``moveend`` paths — NOT a second listener
 *      (it piggybacks the viewport-sync callback already wired for the
 *      snapshot/SSE re-subscribe).
 *
 *   2. ``previewHomeNonce`` — a monotonic counter the editor bumps via
 *      :meth:`requestPreviewHome`. The Map subscribes to the nonce and, on
 *      each increment, re-flies to the *current* draft home (the live
 *      working-state ``map.home``, which the editor's live preview overlay
 *      already reflects). A nonce (rather than a viewport payload) keeps the
 *      store ignorant of the home shape and lets the Map reuse its existing
 *      ``homeViewport`` + ``flyTo`` path — so "Preview home" works even when
 *      the draft home is byte-identical to the last flight (the 6a
 *      home-change effect, which keys on reference equality, would otherwise
 *      no-op after the operator panned away).
 *
 * Both channels are deliberately tiny and reference-stable between writes,
 * matching the sibling stores' render-churn discipline (REQ-UI-027).
 *
 * Cross-references:
 *   - REQ-UI-027 (Zustand for client state).
 *   - REQ-UI-076 (Profile editor — live preview of map config).
 *   - REQ-UI-090 (nav-home → Profile home); ADR-033 §9.4.
 *   - map/Map.tsx (the publisher + the previewHome subscriber).
 *   - profiles/profile-editor.tsx (the Map section consumer).
 */
import { create } from "zustand";

/**
 * A WGS-84 camera snapshot in the Profile ``map.home`` shape
 * (``{ center: { lon, lat }, zoom }``; ADR-033 §9.4) so the editor can
 * write it straight into ``draft.map.home`` with no conversion.
 */
export interface MapViewportSnapshot {
  /** Map center in decimal degrees. */
  readonly center: { readonly lon: number; readonly lat: number };
  /** Current zoom level. */
  readonly zoom: number;
}

/** The map-viewport store shape. */
export interface MapViewportState {
  /**
   * The Map's latest camera, or ``null`` before the map has loaded (so the
   * editor can disable "Set home to current view" until a camera exists).
   */
  readonly viewport: MapViewportSnapshot | null;
  /**
   * A monotonic counter bumped by :meth:`requestPreviewHome`. The Map
   * subscribes and re-flies to the draft home on each increment.
   */
  readonly previewHomeNonce: number;
  /**
   * Publish the Map's current camera. Called by :file:`Map.tsx` on the map's
   * ``load`` event and on every settled pan/zoom (piggybacking the existing
   * viewport-sync callback — no second ``moveend`` listener).
   */
  readonly setViewport: (viewport: MapViewportSnapshot) => void;
  /**
   * Request a "Preview home" re-fly. Bumps :attr:`previewHomeNonce`; the Map
   * flies to the *current* draft home on the resulting change. The editor
   * calls this from its "Preview home" button.
   */
  readonly requestPreviewHome: () => void;
  /** Reset both channels (e.g., on sign-out / network switch / map unmount). */
  readonly clear: () => void;
}

/**
 * The module-level map-viewport store. The Map writes via
 * ``useMapViewportStore.getState().setViewport(...)`` and subscribes to the
 * nonce; the editor reads :attr:`viewport` and calls
 * :meth:`requestPreviewHome`. Consume the camera via :func:`useMapViewport`.
 */
export const useMapViewportStore = create<MapViewportState>((set) => ({
  viewport: null,
  previewHomeNonce: 0,
  setViewport: (viewport) => {
    set({ viewport });
  },
  requestPreviewHome: () => {
    set((state) => ({ previewHomeNonce: state.previewHomeNonce + 1 }));
  },
  clear: () => {
    set({ viewport: null, previewHomeNonce: 0 });
  },
}));

/**
 * The Map's current camera snapshot, or ``null`` before first load. A narrow
 * selector so a consumer (the editor's "Set home to current view" button)
 * re-renders only when the camera actually changes, not on a previewHome
 * bump.
 */
export function useMapViewport(): MapViewportSnapshot | null {
  return useMapViewportStore((state) => state.viewport);
}
